import { DutyInput, DutyOutput, FriCalculationResult, FriDefaults, FriMode } from '@/types/fatigue';
import { evaluatePolicy } from '@/lib/fatiguePolicy';

const MINUTES_PER_DAY = 24 * 60;
const HOURS_PER_DAY = 24;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_FRI_DEFAULTS: FriDefaults = {
  commuteMinutes: 80,
  jobWorkload: 2,
  jobAttention: 1,
  breakFrequencyMinutes: 180,
  breakAverageLengthMinutes: 15,
  continuousWorkLengthMinutes: 360,
  breakAfterContinuousMinutes: 30,
};

type DutyFactorResult = {
  fatigueFactor: number;
  riskFactor: number;
};

type NormalizedDuty = {
  id: string;
  startDayIndex: number;
  startMinutes: number;
  endMinutes: number;
  startTime: number;
  endTime: number;
  startHours: number;
  endHours: number;
  dutyLengthHours: number;
  restLengthHours: number | null;
  averageDutyPerDayHours: number | null;
  commuteMinutes: number;
  jobWorkload: number;
  jobAttention: number;
  breakFrequencyMinutes: number;
  breakAverageLengthMinutes: number;
  continuousWorkLengthMinutes: number;
  breakAfterContinuousMinutes: number;
  cumulativeFatigue: number;
  cumulativeRisk: number;
  dutyTimingComponent: number;
  jobBreaksComponent: number;
  startDayRelative: number;
};

type ParsedDuty = {
  id: string;
  dayIndex: number;
  startMinutes: number;
  endMinutes: number;
  dayRaw: string;
};

export const calculateFriSchedule = (
  duties: DutyInput[],
  defaults: FriDefaults,
  mode: FriMode,
): FriCalculationResult => {
  const errors: string[] = [];
  const parsed = parseAndNormalizeInputs(duties, errors);
  if (parsed.length === 0) {
    return { duties: [], errors, policyFindings: [] };
  }

  const sorted = sortParsedDuties(parsed);
  const normalized = buildNormalizedDuties(sorted, duties, defaults, mode, errors);
  if (normalized.length === 0) {
    return { duties: [], errors, policyFindings: [] };
  }

  computeCumulativeComponents(normalized);

  const outputs: DutyOutput[] = normalized.map(duty => {
    const cumulativeComponent = mode === 'risk'
      ? duty.cumulativeRisk * 0.98899
      : duty.cumulativeFatigue * 100;
    const index = mode === 'risk'
      ? cumulativeComponent * duty.dutyTimingComponent * duty.jobBreaksComponent
      : 100 * (1 - (1 - cumulativeComponent / 100) * (1 - duty.dutyTimingComponent / 100 - duty.jobBreaksComponent / 100));

    return {
      dutyLengthHours: duty.dutyLengthHours,
      restLengthHours: duty.restLengthHours,
      averageDutyPerDayHours: duty.averageDutyPerDayHours,
      cumulativeComponent,
      dutyTimingComponent: duty.dutyTimingComponent,
      jobBreaksComponent: duty.jobBreaksComponent,
      index,
    };
  });

  const policyFindings = evaluatePolicy(normalized);
  return { duties: outputs, errors, policyFindings };
};

export const sortDutyInputs = (duties: DutyInput[]): DutyInput[] => {
  const errors: string[] = [];
  const parsed = parseAndNormalizeInputs(duties, errors);
  if (parsed.length === 0) {
    return duties;
  }
  const sorted = sortParsedDuties(parsed);
  const byId = new Map(duties.map(duty => [duty.id, duty]));
  return sorted.map(item => byId.get(item.id)).filter(Boolean) as DutyInput[];
};

export const formatHoursMinutes = (hours: number): string => {
  if (hours <= 0) {
    return 'Period < 1 minute';
  }
  let remaining = hours;
  const days = Math.floor(remaining / HOURS_PER_DAY);
  remaining -= days * HOURS_PER_DAY;
  let hrs = Math.floor(remaining);
  remaining -= hrs;
  let minutes = roundHalfEven(remaining * 60, 0);
  if (minutes >= 60) {
    hrs += 1;
    minutes = 0;
  }
  let output = '';
  if (minutes > 0) {
    output = `${minutes}m`;
  }
  if (hrs > 0) {
    output = `${hrs}h ${output}`.trim();
  }
  if (days > 0) {
    output = `${days}d ${output}`.trim();
  }
  return output;
};

const parseAndNormalizeInputs = (duties: DutyInput[], errors: string[]): ParsedDuty[] => {
  return duties.flatMap(duty => {
    if (!duty.day || !duty.onDuty || !duty.offDuty) {
      return [];
    }
    const dayIndex = parseDayToIndex(duty.day);
    if (dayIndex === null) {
      errors.push(`Invalid day/date value for duty ${duty.day}`);
      return [];
    }
    const startMinutes = parseTimeToMinutes(duty.onDuty);
    const endMinutes = parseTimeToMinutes(duty.offDuty);
    if (startMinutes === null || endMinutes === null) {
      errors.push(`Invalid time value for duty on ${duty.day}`);
      return [];
    }
    return [{
      id: duty.id,
      dayIndex,
      startMinutes,
      endMinutes,
      dayRaw: duty.day,
    }];
  });
};

const sortParsedDuties = (duties: ParsedDuty[]): ParsedDuty[] => {
  return [...duties].sort((a, b) => {
    if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
    if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
    return a.endMinutes - b.endMinutes;
  });
};

const buildNormalizedDuties = (
  parsed: ParsedDuty[],
  rawInputs: DutyInput[],
  defaults: FriDefaults,
  mode: FriMode,
  errors: string[],
): NormalizedDuty[] => {
  const byId = new Map(rawInputs.map(duty => [duty.id, duty]));
  const normalized: NormalizedDuty[] = [];
  let scheduleStartDayIndex: number | null = null;
  let previousEndTime = 0;
  let totalDurationOfWork = 0;
  let totalWorkDays = 0;

  parsed.forEach((item, index) => {
    const input = byId.get(item.id);
    if (!input) {
      return;
    }

    const startTime = item.dayIndex + item.startMinutes / MINUTES_PER_DAY;
    let endTime = item.dayIndex + item.endMinutes / MINUTES_PER_DAY;
    if (endTime < startTime) {
      endTime += 1;
    }
    const dutyLengthHours = (endTime - startTime) * HOURS_PER_DAY;
    if (dutyLengthHours <= 0) {
      errors.push(`Invalid duty length on ${item.dayRaw}`);
      return;
    }

    if (scheduleStartDayIndex === null) {
      scheduleStartDayIndex = Math.floor(startTime);
    }
    const restLengthHours = index === 0
      ? (startTime - scheduleStartDayIndex) * HOURS_PER_DAY
      : (startTime - previousEndTime) * HOURS_PER_DAY;

    if (index === 0) {
      totalWorkDays = (Number.isInteger(endTime) ? Math.floor(endTime) - scheduleStartDayIndex : Math.floor(endTime) - scheduleStartDayIndex + 1);
    } else {
      totalWorkDays = (Number.isInteger(endTime) ? Math.floor(endTime) - (scheduleStartDayIndex ?? 0) : Math.floor(endTime) - (scheduleStartDayIndex ?? 0) + 1);
    }
    totalDurationOfWork += dutyLengthHours;
    const averageDutyPerDayHours = totalWorkDays > 0 ? totalDurationOfWork / totalWorkDays : null;

    const commuteMinutes = input.useDefaultCommute
      ? defaults.commuteMinutes
      : (input.commuteMinutes ?? defaults.commuteMinutes);
    const modelCommuteMinutes = commuteMinutes / 2;
    const jobWorkload = input.useDefaultJobBreaks
      ? defaults.jobWorkload
      : (input.jobWorkload ?? defaults.jobWorkload);
    const jobAttention = input.useDefaultJobBreaks
      ? defaults.jobAttention
      : (input.jobAttention ?? defaults.jobAttention);
    const breakFrequencyMinutes = input.useDefaultJobBreaks
      ? defaults.breakFrequencyMinutes
      : (input.breakFrequencyMinutes ?? defaults.breakFrequencyMinutes);
    const breakAverageLengthMinutes = input.useDefaultJobBreaks
      ? defaults.breakAverageLengthMinutes
      : (input.breakAverageLengthMinutes ?? defaults.breakAverageLengthMinutes);
    const continuousWorkLengthMinutes = input.useDefaultJobBreaks
      ? defaults.continuousWorkLengthMinutes
      : (input.continuousWorkLengthMinutes ?? defaults.continuousWorkLengthMinutes);
    const breakAfterContinuousMinutes = input.useDefaultJobBreaks
      ? defaults.breakAfterContinuousMinutes
      : (input.breakAfterContinuousMinutes ?? defaults.breakAfterContinuousMinutes);

    const dutyFactor = calculateDutyFactor(
      dutyLengthHours,
      jobWorkload,
      jobAttention,
      breakFrequencyMinutes,
      breakAverageLengthMinutes,
      continuousWorkLengthMinutes,
      breakAfterContinuousMinutes,
    );

    let dutyTimingComponent = 0;
    let jobBreaksComponent = 0;
    if (mode === 'risk') {
      dutyTimingComponent = (associatedRisk(item.startMinutes / 60, item.endMinutes / 60, modelCommuteMinutes) / 1.288) * 0.997976;
      jobBreaksComponent = (dutyFactor.riskFactor / 1.032) / 1.0286182;
    } else {
      const pt = threeProcessEstimation(item.startMinutes / 60, dutyLengthHours, dutyFactor.fatigueFactor, modelCommuteMinutes);
      let p0 = threeProcessEstimation(item.startMinutes / 60, dutyLengthHours, 0.14, modelCommuteMinutes);
      if (p0 > pt) p0 = pt;
      dutyTimingComponent = p0 * 100;
      jobBreaksComponent = (pt - p0) * 100;
    }

    const startDayRelative = Math.floor(startTime) - scheduleStartDayIndex + 1;

    normalized.push({
      id: input.id,
      startDayIndex: item.dayIndex,
      startMinutes: item.startMinutes,
      endMinutes: item.endMinutes,
      startTime,
      endTime,
      startHours: item.startMinutes / 60,
      endHours: item.endMinutes / 60,
      dutyLengthHours,
      restLengthHours: index === 0 ? null : restLengthHours,
      averageDutyPerDayHours,
      commuteMinutes,
      jobWorkload,
      jobAttention,
      breakFrequencyMinutes,
      breakAverageLengthMinutes,
      continuousWorkLengthMinutes,
      breakAfterContinuousMinutes,
      cumulativeFatigue: 0,
      cumulativeRisk: 0,
      dutyTimingComponent,
      jobBreaksComponent,
      startDayRelative,
    });

    previousEndTime = endTime;
  });

  return normalized;
};

const calculateDutyFactor = (
  dutyLengthHours: number,
  jobWorkload: number,
  jobAttention: number,
  breakFrequencyMinutes: number,
  breakAverageLengthMinutes: number,
  continuousWorkLengthMinutes: number,
  breakAfterContinuousMinutes: number,
): DutyFactorResult => {
  const Constant1 = 1.826297414;
  const Constant2 = 1.146457295;

  const workloadSum = jobWorkload + jobAttention;
  const continuousWork = (breakFrequencyMinutes + continuousWorkLengthMinutes) / 2;
  const durationOfBreak = (breakAverageLengthMinutes + breakAfterContinuousMinutes) / 2;
  let workload = 0.125 + 0.015 * workloadSum;

  if (durationOfBreak === 0 && continuousWork === 0) {
    workload *= 1.2;
  } else {
    workload = workload * 1.2 * continuousWork / (continuousWork + durationOfBreak);
  }

  const fiveMinBreak = 0.172731235 + -0.200918653 * 0.04 + 0.03552264 * 0.04 * 0.04;
  const fifteenMinBreak = continuousWork * 0.000442;
  const thirtyMinBreak = 0.000206 + -0.0000138433 * continuousWork + 0.00000096491 * continuousWork * continuousWork;

  let finalBreakFactor = 0;
  if (durationOfBreak < 5) {
    finalBreakFactor = 0.174 + (fiveMinBreak - 0.174) * durationOfBreak / 5;
  } else if (durationOfBreak < 15) {
    finalBreakFactor = fiveMinBreak + (fifteenMinBreak - fiveMinBreak) * (durationOfBreak - 5) / 10;
  } else if (durationOfBreak < 30) {
    finalBreakFactor = fifteenMinBreak + (thirtyMinBreak - fifteenMinBreak) * (durationOfBreak - 15) / 15;
  } else if (durationOfBreak < 60) {
    finalBreakFactor = thirtyMinBreak * (60 - durationOfBreak) / 30;
  } else {
    finalBreakFactor = 0;
  }

  const fatigueFactor = finalBreakFactor + workload;

  const lengthOfDuty = dutyLengthHours * 60;
  const numberOfSequences = 1 + Math.floor(lengthOfDuty / (continuousWork + durationOfBreak) - 0.01);
  const lengthOfSequences = Math.floor(0.5 + continuousWork / 15);
  const breakEffect = 0.24 * Math.exp(-0.395 * (durationOfBreak - 13.3)) / 0.2388 / (1 + Math.exp(-0.395 * (durationOfBreak - 13.3)));

  let n = 0;
  let leng = -1;
  let rr = 1;
  let rrTotal = 0;

  do {
    if (leng < lengthOfSequences) {
      leng += 1;
    } else {
      leng = 0;
    }
    if (leng === 0) {
      rr = 1 + (rr - 1) * breakEffect;
    } else {
      rr = Constant1 + (rr - Constant1) * Math.exp(-0.25 * Constant2);
    }
    rrTotal += rr;
    n += 1;
  } while (n <= numberOfSequences * lengthOfSequences);

  let riskFactor = rrTotal / n / 1.4858;
  riskFactor += (workloadSum - 3) * 0.0232;

  return { fatigueFactor, riskFactor };
};

const associatedRisk = (startHours: number, endHours: number, commuteMinutes: number): number => {
  const P1 = -0.4287;
  const P2 = 0.1501;
  const P3 = 0.129;
  const P4 = 0.0359;
  const P5 = -0.8012;
  const P6 = 0.7315;

  const dutyLength = endHours > startHours ? endHours - startHours : HOURS_PER_DAY + endHours - startHours;
  const adjStart = startHours - ((commuteMinutes - 40) / 60);
  const relativeCommute = (commuteMinutes - 40) / 60;
  const adjLength = dutyLength + relativeCommute;

  const todRisk = 1.0106 + 0.1057 * (Math.sin(Math.PI * endHours / 12) - Math.sin(Math.PI * adjStart / 12)) / (adjLength * Math.PI / 12);
  let shiftRisk = 1;
  if (adjLength < 4.25) {
    shiftRisk = 1 + P4 + P5 * Math.exp(-P6 * adjLength);
  } else if (adjLength > 8.13) {
    shiftRisk = 1 + P1 + P2 * Math.exp(P3 * adjLength);
  }

  let commuteRisk = 1;
  if (relativeCommute < 4.25) {
    commuteRisk = 1 + P4 + P5 * Math.exp(-P6 * relativeCommute);
  } else if (relativeCommute > 8.13) {
    commuteRisk = 1 + P1 + P2 * Math.exp(P3 * relativeCommute);
  }

  const shiftRiskAdjusted = relativeCommute <= 0
    ? shiftRisk
    : (shiftRisk * adjLength - commuteRisk * relativeCommute) / dutyLength;

  return todRisk * shiftRiskAdjusted;
};

const threeProcessEstimation = (
  dutyStartHours: number,
  dutyLengthHours: number,
  timeOnDuty: number,
  commuteMinutes: number,
): number => {
  const startAmplitude = 0.5;
  const startPhase = 1.25;
  const circadianAmplitude = 0.74;
  const circadianPhase = 5.23;

  const eAmplitude = startAmplitude * startAmplitude + circadianAmplitude * circadianAmplitude
    - startAmplitude * circadianAmplitude * Math.cos((startPhase - circadianPhase) * Math.PI / 12);
  const ePhase = excelAtan2(
    startAmplitude * Math.cos(startPhase * Math.PI / 12) - circadianAmplitude * Math.cos(circadianPhase * Math.PI / 12),
    startAmplitude * Math.sin(startPhase * Math.PI / 12) - circadianAmplitude * Math.sin(circadianPhase * Math.PI / 12),
  ) * 12 / Math.PI;

  const startTime = eAmplitude * Math.cos((dutyStartHours - ePhase) * Math.PI / 12);

  let dutyHours = dutyStartHours;
  let n = 0;
  let pKss8Total = 0;

  const steps = Math.round(dutyLengthHours / 0.25);
  for (let step = 0; step <= steps; step += 1) {
    const duration = step * 0.25;
    n += 1;
    const tod = (duration + ((commuteMinutes - 30) / 60)) * timeOnDuty;
    const cr = circadianAmplitude * Math.cos((dutyHours - circadianPhase) * Math.PI / 12);
    const total = tod + startTime + cr;
    const sp = total + 2.45;
    const spcc = 1 + 6 / (1 + Math.exp(3.057 - 0.764 * sp));
    const kss = -0.6 + 1.436 * spcc;
    const pKss8 = 1.26 / (1 + 3670 * Math.exp(-1.06 * kss));
    pKss8Total += pKss8;

    if (dutyHours + 1 >= 24) {
      dutyHours -= 23.75;
    } else {
      dutyHours += 0.25;
    }
  }

  return n > 0 ? pKss8Total / n : 0;
};

const computeCumulativeComponents = (duties: NormalizedDuty[]) => {
  const count = duties.length;
  if (count === 0) return;

  const cumDuty = duties.map(duty => ({
    startCommute: ((duty.startTime - Math.floor(duty.startTime)) * HOURS_PER_DAY) - (60 - 30) / 60,
    endCommute: 0,
    startDay: duty.startDayRelative,
    night: 0,
    night2: 0,
    lossn1: 0,
    loss2xg: 0,
    ngtt: 0,
    startHr: 0,
    length: 0,
    gap: 0,
    cr: 0,
    risk: 0,
    pvt3: 0,
    pvt4: 0,
    pvtNext: 0,
  }));

  let dayCount = 0;
  cumDuty.forEach((item, index) => {
    const duty = duties[index];
    if (item.startCommute < 15) {
      item.night = duty.startDayRelative;
    } else {
      item.night = duty.startDayRelative + 1;
    }
    if (index === count - 1) {
      dayCount = item.night;
    }
  });

  const cumDay = Array.from({ length: dayCount }, (_, i) => ({
    night: i + 1,
    nextNight: i + 2,
    nightPVT: 0,
    pvtNext: 0,
  }));

  for (let i = 0; i < count; i += 1) {
    const duty = duties[i];
    const item = cumDuty[i];
    item.endCommute = ((duty.endTime - Math.floor(duty.endTime)) * HOURS_PER_DAY) + (60 - 30) / 60;

    if (roundHalfEven(item.endCommute, 2) > roundHalfEven(item.startCommute, 2)) {
      item.length = item.endCommute - item.startCommute;
    } else {
      item.length = (HOURS_PER_DAY + item.endCommute) - item.startCommute;
    }

    if (i < count - 1) {
      const next = cumDuty[i + 1];
      item.gap = HOURS_PER_DAY * (next.startDay - item.startDay) + next.startCommute - item.startCommute - item.length;
    } else {
      item.gap = HOURS_PER_DAY;
    }

    item.startHr = item.startCommute + (item.startDay - item.night + 1) * HOURS_PER_DAY;
    const endHr = item.startHr + item.length;

    const loss1 = item.startHr > 22.76
      ? (0.03501 * (37.5 - item.startHr) + 0.01928) * (37.5 - item.startHr)
      : 0;

    const bedTime = endHr < 24.25 ? 16.3 + 0.367 * endHr : endHr + 1;
    const rkFit = endHr < 18.5
      ? 8.13
      : -2.28827436527851 + 11.7995318577367 / (1 + 0.472949055173571 * Math.exp(-1.77393493516727 + 0.16244804759197 * (bedTime - 20)));
    const aircrewFit = 8 - 0.285 * (endHr - 18.5) < 8 ? 8 - 0.285 * (endHr - 18.5) : 8;
    const loss2 = 8.07 - (rkFit + aircrewFit) / 2 > 8 ? 8 : 8.07 - (rkFit + aircrewFit) / 2;
    const loss2g = loss2 < 9 - item.gap ? 9 - item.gap : loss2;
    let lossn1 = loss2g;
    if (!(item.startHr >= 15 && item.startHr <= 22.67)) {
      lossn1 = loss1 < loss2g ? loss1 : loss2g;
    }
    item.lossn1 = lossn1;

    const enxHr = endHr - 24;
    const bedTimeX = enxHr < 24.25 ? 16.3 + 0.367 * enxHr : enxHr + 1;
    const rkFitx = enxHr < 18.5
      ? 8.13
      : -2.28827436527851 + 11.7995318577367 / (1 + 0.472949055173571 * Math.exp(-1.77393493516727 + 0.16244804759197 * (bedTimeX - 20)));
    const aircrewx = 8 < 8 - 0.285 * (enxHr - 18.5) ? 8 : 8 - 0.285 * (enxHr - 18.5);
    item.night2 = item.night + 1;
    const loss2x = 8.07 - (rkFitx + aircrewx) / 2 > 8 ? 8 : 8.07 - (rkFitx + aircrewx) / 2;
    const loss2xg = loss2x < 9 - item.gap ? 9 - item.gap : loss2x;
    item.loss2xg = loss2xg;

    const lastn = i < count - 1 ? (cumDuty[i + 1].night === item.night ? 0 : 1) : 1;
    item.ngtt = lastn === 1 ? item.night : 0;
  }

  for (let dayIndex = 0; dayIndex < dayCount; dayIndex += 1) {
    const night = dayIndex + 1;
    let loss = -99;
    for (let i = 0; i < count; i += 1) {
      if (cumDuty[i].night === night) {
        loss = cumDuty[i].lossn1;
        break;
      }
    }
    const lossc = loss === -99 ? 0 : loss;
    let lossn2 = 0;
    if (night !== 1) {
      lossn2 = -99;
      for (let i = 0; i < count; i += 1) {
        if (cumDuty[i].night2 === night) {
          lossn2 = cumDuty[i].loss2xg;
          break;
        }
      }
    }
    const lossn2c = lossn2 > -99 ? lossn2 : 0;
    const totalLoss = lossc + lossn2c > 8 ? 8 : lossc + lossn2c;
    const asymptote = 3.9 - totalLoss * 0.441596758431994;

    let nightPvt = 0;
    if (night === 1) {
      if (3.87 > asymptote) {
        nightPvt = asymptote + (3.87 - asymptote) * Math.exp(-0.136934833451947);
      } else {
        nightPvt = asymptote + (3.87 - asymptote) * Math.exp(-0.686825862760272);
      }
    } else {
      const prev = cumDay[dayIndex - 1].nightPVT;
      if (prev > asymptote) {
        nightPvt = asymptote + (prev - asymptote) * Math.exp(-0.136934833451947);
      } else {
        nightPvt = asymptote + (prev - asymptote) * Math.exp(-0.686825862760272);
      }
    }
    cumDay[dayIndex].nightPVT = nightPvt;
  }

  for (let dayIndex = dayCount - 1; dayIndex >= 0; dayIndex -= 1) {
    cumDay[dayIndex].pvtNext = dayIndex === dayCount - 1 ? 0 : cumDay[dayIndex + 1].nightPVT;
  }

  for (let i = 0; i < count; i += 1) {
    let cumPvt = 0;
    if (i === 0) {
      cumPvt = 3.9;
    } else {
      let n = 0;
      while (n < dayCount && cumDay[n].nextNight !== cumDuty[i].ngtt) {
        n += 1;
      }
      cumPvt = n === dayCount ? 0 : cumDay[n].nightPVT;
    }

    if (i === 0) {
      cumDuty[i].pvtNext = 3.9;
    } else {
      let n = 0;
      while (n < dayCount && cumDay[n].nextNight !== cumDuty[i].ngtt) {
        n += 1;
      }
      cumDuty[i].pvtNext = n === dayCount ? 0 : cumDay[n].pvtNext;
    }

    const startHr = cumDuty[i].startHr;
    let pvt2 = cumPvt;
    if (startHr < 39 && startHr > 27) {
      pvt2 = cumPvt + (cumDuty[i].pvtNext - cumPvt) * 0.5 * (1 + Math.sin(Math.PI * (startHr - 33) / 12));
    }
    cumDuty[i].pvt3 = (cumDuty[i].pvtNext === 0 && cumPvt === 0) ? 0 : pvt2 * 4.44 / 3.9;
  }

  for (let i = count - 1; i >= 0; i -= 1) {
    if (cumDuty[i].pvt3 === 0 && i + 1 < count) {
      cumDuty[i].pvt4 = cumDuty[i + 1].pvt4;
    } else {
      cumDuty[i].pvt4 = cumDuty[i].pvt3;
    }
  }

  for (let i = 0; i < count; i += 1) {
    let x = 1;
    if (i > 0) {
      const gap = cumDuty[i - 1].gap;
      if (gap > 9) {
        x = 1;
      } else if (gap < 1) {
        x = 0;
      } else {
        x = (gap - 1) / 8;
      }
    }

    const pvtOut = cumDuty[i].pvtNext > 0
      ? x * cumDuty[i].pvt4 + (1 - x) * cumDuty[i].pvtNext
      : cumDuty[i].pvt4;
    const sp = Math.max(1, Math.log((1.001 - pvtOut / 4.44) / 0.00189) / 0.801);
    const spcc = 1 + 6 / (1 + Math.exp(3.057 - 0.764 * sp));
    const pKss = 1.26 / (1 + 3670 * Math.exp(-1.06 * (-0.6 + 1.436 * spcc)));
    duties[i].cumulativeFatigue = pKss / 3;

    cumDuty[i].cr = 0.0886 + 0.0359 * Math.cos(Math.PI * (cumDuty[i].startCommute + 0.5 * cumDuty[i].length) / 12);

    const lGap = i === 0 ? 24 : cumDuty[i - 1].gap;
    const endd = i === 0 ? 0 : cumDuty[i - 1].endCommute;
    const nd = Math.floor(lGap / 24);
    const en1 = i === 0 ? 0 : (endd + 1 >= 24 ? endd - 23 : endd + 1);
    const st1 = cumDuty[i].startCommute + 1 >= 24 ? cumDuty[i].startCommute - 23 : cumDuty[i].startCommute + 1;
    const endSleep = en1 < 8 ? 8 - en1 : 0;
    const startSleep = st1 < 8 ? st1 : 0;
    const sleep2 = endSleep > 0 && startSleep > 0 ? 1 : 0;
    const nslp1 = nd + (endSleep + startSleep) / 8;
    const nslp2 = sleep2 === 1 && st1 >= en1 ? nslp1 - 2 : nslp1;

    if (i === 0) {
      cumDuty[i].risk = 1;
    } else {
      if (lGap < 9) {
        cumDuty[i].risk = cumDuty[i - 1].risk + 0.06 * (9 - lGap);
      } else if (lGap > 24) {
        cumDuty[i].risk = 1 + (cumDuty[i - 1].risk - 1) * Math.exp(-1.118 * (nslp2 - 1));
      } else {
        cumDuty[i].risk = cumDuty[i - 1].risk + 0.5 * (cumDuty[i].cr + cumDuty[i - 1].cr);
      }
    }

    duties[i].cumulativeRisk = cumDuty[i].risk / 1.113;
  }
};

const parseDayToIndex = (raw: string): number | null => {
  const value = raw.trim();
  if (!value) return null;
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
    return Math.floor(numeric);
  }
  const dateParts = parseDateParts(value);
  if (!dateParts) return null;
  const { year, month, day } = dateParts;
  const utc = Date.UTC(year, month - 1, day);
  return Math.floor(utc / MS_PER_DAY);
};

const parseDateParts = (value: string): { year: number; month: number; day: number } | null => {
  if (value.includes('-')) {
    const [yearRaw, monthRaw, dayRaw] = value.split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if ([year, month, day].some(Number.isNaN)) return null;
    return { year, month, day };
  }
  if (value.includes('/')) {
    const [dayRaw, monthRaw, yearRaw] = value.split('/');
    let year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if ([year, month, day].some(Number.isNaN)) return null;
    if (year < 100) {
      year += year < 50 ? 2000 : 1900;
    }
    return { year, month, day };
  }
  return null;
};

const parseTimeToMinutes = (raw: string): number | null => {
  const value = raw.trim();
  if (!value) return null;
  let hours = 0;
  let minutes = 0;
  if (value.includes(':')) {
    const [hRaw, mRaw] = value.split(':');
    hours = Number(hRaw);
    minutes = Number(mRaw);
  } else if (value.length === 4 && Number.isFinite(Number(value))) {
    hours = Number(value.slice(0, 2));
    minutes = Number(value.slice(2, 4));
  } else {
    return null;
  }
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  if (hours === 24 && minutes === 0) {
    return 0;
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const roundHalfEven = (value: number, decimals = 0): number => {
  const factor = 10 ** decimals;
  const n = value * factor;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const floor = Math.floor(abs);
  const diff = abs - floor;
  const epsilon = 1e-10;
  let rounded = floor;
  if (diff > 0.5 + epsilon) {
    rounded = floor + 1;
  } else if (diff < 0.5 - epsilon) {
    rounded = floor;
  } else {
    rounded = floor % 2 === 0 ? floor : floor + 1;
  }
  return sign * rounded / factor;
};

const excelAtan2 = (x: number, y: number): number => {
  return Math.atan2(y, x);
};

export const internalMath = {
  roundHalfEven,
  excelAtan2,
  parseDayToIndex,
  parseTimeToMinutes,
};
